import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Coins, Activity, Users, Cpu, Sparkles, ArrowUp,
  Calendar, Search, Download, TrendingUp, AlertTriangle, ChevronUp, ChevronDown, ChevronsUpDown, Eye, X,
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import toast from 'react-hot-toast';
import LineChart from '@/components/reports/charts/LineChart';
import SimpleSelect from '@/components/ui/SimpleSelect';
import Pagination from '@/components/ui/Pagination';
import { formatDate, formatDateTime } from '@/utils/dateFormat';
import {
  KpiCard, fmtNumber, fmtTokens, fmtCredits, fmtCurrency, featureLabel, FEATURE_LABELS, PRESETS,
  autoGranularity, buildPeriodKeys, formatPeriodLabel,
} from './aiCostShared';
import tenantUsageService, {
  CostType, TenantUsageFilter, TenantUsageSummary, TenantUsageRecord, TenantUsageTrendPoint,
} from '@/services/tenantUsageService';
import { aiModelSettingsService } from '@/services/ai-model-settings.service';
import type { AIModelCatalog } from '@/types/ai-model-settings.types';

// ── local helpers ────────────────────────────────────────────────────────────
const today = () => format(new Date(), 'yyyy-MM-dd');
const daysAgo = (n: number) => format(subDays(new Date(), n), 'yyyy-MM-dd');
const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5';

/** "Name (email)" — the app-standard user label; falls back gracefully. */
const userDisplay = (name?: string, email?: string) =>
  (email && name && name !== email ? `${name} (${email})` : (name || email || 'Unknown'));

/** Compact Y-axis tick (no unit) so labels never wrap/clip — the unit stays in the tooltip. */
const axisNum = (v: number) =>
  v >= 1_000_000 ? `${(v / 1e6).toFixed(1)}M`
    : v >= 1_000 ? `${(v / 1e3).toFixed(1)}K`
    : Number.isInteger(v) ? String(v) : v.toFixed(2);

type TrendMetric = 'cost' | 'tokens' | 'requests';

interface FilterState {
  start_date: string; end_date: string;
  preset: string;                       // '7d' | '30d' | … | 'all' | 'custom'
  feature_type: string; model: string;
  status: '' | 'success' | 'failed';
  search: string;
  cost_type: CostType;
}

/** Section panel — matches the app's card style. */
function Panel({ icon, title, right, children }: { icon: React.ReactNode; title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center">{icon}</span>
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function NoData({ label = 'No usage in this range' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-center">
      <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center mb-3"><Activity className="w-5 h-5 text-gray-400" /></div>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: 'success' | 'failed' }) {
  return status === 'failed'
    ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-600">Failed</span>
    : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-600">Success</span>;
}

const DEFAULT_PAGE_SIZE = 20;

export default function TenantAiUsage() {
  const [filter, setFilter] = useState<FilterState>({
    start_date: daysAgo(29), end_date: today(), preset: '30d',   // Last 30 days = 30 inclusive days
    feature_type: '', model: '', status: '', search: '', cost_type: 'credit',
  });
  const [trendMetric, setTrendMetric] = useState<TrendMetric>('cost');

  const [summary, setSummary] = useState<TenantUsageSummary | null>(null);
  const [trend, setTrend] = useState<TenantUsageTrendPoint[]>([]);
  const [records, setRecords] = useState<TenantUsageRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<{ field: string; order: 'asc' | 'desc' }>({ field: 'createdAt', order: 'desc' });
  const [detail, setDetail] = useState<TenantUsageRecord | null>(null);   // record shown in the eye-drawer
  const [catalogModels, setCatalogModels] = useState<string[]>([]);       // all available model ids (from AI Model Settings)

  const [loadingTop, setLoadingTop] = useState(true);   // summary + trend
  const [loadingRows, setLoadingRows] = useState(true);  // table
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounce the free-text search so we don't fire a request per keystroke.
  const [searchInput, setSearchInput] = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setFilter((f) => ({ ...f, search: searchInput.trim() })), 400);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [searchInput]);

  // The Model filter lists ALL available models (from AI Model Settings), not only the ones
  // already used — so a tenant can filter by any model. Fetched once on mount.
  useEffect(() => {
    aiModelSettingsService.getCatalog()
      .then((resp: any) => {
        const cat = (resp?.data ?? {}) as AIModelCatalog;
        const ids = [...(cat.text || []), ...(cat.image || []), ...(cat.embedding || [])].map((m) => m.id);
        setCatalogModels(ids);
      })
      .catch(() => setCatalogModels([]));
  }, []);

  const isCredit = filter.cost_type === 'credit';
  const fmtCostVal = useCallback((v = 0) => (isCredit ? fmtCredits(v) : fmtCurrency(v)), [isCredit]);

  // Period filter (date range + unit) — the GLOBAL control: drives the summary cards AND the trend.
  const periodFilter = useMemo<TenantUsageFilter>(() => ({
    start_date: filter.start_date || undefined,
    end_date: filter.end_date || undefined,
    cost_type: filter.cost_type,
  }), [filter.start_date, filter.end_date, filter.cost_type]);

  // Table filter = period + table-only refinements (feature / model / status / search).
  const tableFilter = useMemo<TenantUsageFilter>(() => ({
    ...periodFilter,
    feature_types: filter.feature_type ? [filter.feature_type] : undefined,
    models: filter.model ? [filter.model] : undefined,
    status: filter.status || undefined,
    search: filter.search || undefined,
  }), [periodFilter, filter.feature_type, filter.model, filter.status, filter.search]);

  const granularity = useMemo(() => autoGranularity(filter.start_date, filter.end_date), [filter.start_date, filter.end_date]);

  // Summary + trend depend only on the PERIOD (not the table refinements).
  useEffect(() => {
    let cancelled = false;
    setLoadingTop(true); setError(null);
    Promise.all([
      tenantUsageService.getSummary(periodFilter),
      tenantUsageService.getTrend({ ...periodFilter, granularity }),
    ]).then(([s, t]) => {
      if (cancelled) return;
      setSummary(s); setTrend(t);
    }).catch((err: any) => {
      if (cancelled) return;
      setError(err?.error?.error_description || err?.message || 'Failed to load usage');
      setSummary(null); setTrend([]);
    }).finally(() => { if (!cancelled) setLoadingTop(false); });
    return () => { cancelled = true; };
  }, [periodFilter, granularity]);

  // Reset to page 1 when any table filter changes.
  useEffect(() => { setPage(1); }, [tableFilter]);

  // Load table records (depends on the table filter, page, sort).
  useEffect(() => {
    let cancelled = false;
    setLoadingRows(true);
    tenantUsageService.getRecords({
      ...tableFilter, skip: (page - 1) * pageSize, limit: pageSize, sort: sort.field, order: sort.order,
    }).then((res) => {
      if (cancelled) return;
      setRecords(res.items); setTotal(res.total);
    }).catch(() => { if (!cancelled) { setRecords([]); setTotal(0); } })
      .finally(() => { if (!cancelled) setLoadingRows(false); });
    return () => { cancelled = true; };
  }, [tableFilter, page, sort]);

  const toggleSort = (field: string) =>
    setSort((s) => s.field === field ? { field, order: s.order === 'asc' ? 'desc' : 'asc' } : { field, order: 'desc' });

  // ── trend chart data ──
  // Continuous axis: EVERY bucket in the range (gaps → 0) with tidy "MMM d" labels — same as AI Cost Tracking.
  const trendKeys = useMemo(
    () => (filter.start_date && filter.end_date)
      ? buildPeriodKeys(filter.start_date, filter.end_date, granularity)
      : [...new Set(trend.map((p) => p.period))].sort(),   // All time → just the returned buckets
    [filter.start_date, filter.end_date, granularity, trend],
  );
  const trendLabels = useMemo(() => trendKeys.map((k) => formatPeriodLabel(k, granularity)), [trendKeys, granularity]);
  const byPeriod = useMemo(() => new Map(trend.map((p) => [p.period, p])), [trend]);
  const trendSeries = useMemo(() => {
    // Tokens view shows Input + Output + Total (Total = input + output), so the trend and
    // tooltip show the full breakdown — same as the AI Cost Tracking token trend.
    if (trendMetric === 'tokens') {
      return [
        { name: 'Input', color: '#22c55e', data: trendKeys.map((k) => byPeriod.get(k)?.prompt_tokens || 0) },
        { name: 'Output', color: '#f59e0b', data: trendKeys.map((k) => byPeriod.get(k)?.completion_tokens || 0) },
        { name: 'Total', color: '#6366f1', data: trendKeys.map((k) => { const p = byPeriod.get(k); return p?.total_tokens ?? ((p?.prompt_tokens || 0) + (p?.completion_tokens || 0)); }) },
      ];
    }
    const pick = trendMetric === 'cost'
      ? (p?: TenantUsageTrendPoint) => p?.cost || 0
      : (p?: TenantUsageTrendPoint) => p?.request_count || 0;
    const color = trendMetric === 'cost' ? '#7c3aed' : '#06b6d4';
    const name = trendMetric === 'cost' ? (isCredit ? 'Credits' : 'Cost') : 'Requests';
    return [{ name, color, data: trendKeys.map((k) => pick(byPeriod.get(k))) }];
  }, [trendKeys, byPeriod, trendMetric, isCredit]);
  const trendFmt = trendMetric === 'cost' ? fmtCostVal : trendMetric === 'tokens' ? fmtTokens : fmtNumber;

  // ── CSV export (current filters, up to 1000 rows) ──
  const exportCsv = async () => {
    setExporting(true);
    const t = toast.loading('Preparing export…');
    try {
      const res = await tenantUsageService.getRecords({ ...tableFilter, skip: 0, limit: 1000, sort: sort.field, order: sort.order, is_export: true });
      const unit = isCredit ? 'Credits' : 'Cost (USD)';
      const head = ['User', 'Email', 'Feature', 'Model', 'Prompt Tokens', 'Completion Tokens', 'Total Tokens', unit, 'Status', 'Date'];
      const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const rows = res.items.map((r) => [
        r.user_name, r.user_email, featureLabel(r.feature_type), r.model,
        r.prompt_tokens, r.completion_tokens, r.total_tokens, r.cost, r.status, formatDateTime(r.date, true),
      ].map(esc).join(','));
      const csv = [head.map(esc).join(','), ...rows].join('\n');
      // Prepend a UTF-8 BOM so Excel decodes non-ASCII (Tamil, etc.) correctly instead of as Latin-1.
      const url = URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url; a.download = `ai-usage-${today()}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.dismiss(t); toast.success(`Exported ${res.items.length} rows`);
    } catch (e: any) {
      toast.dismiss(t); toast.error(e?.error?.error_description || e?.message || 'Export failed');
    } finally { setExporting(false); }
  };

  const clearAll = () => { setSearchInput(''); setFilter((f) => ({ ...f, feature_type: '', model: '', status: '', search: '' })); };

  // Date-range preset handlers (global period control).
  // "Last N days" = N days INCLUSIVE of today → start = today − (N−1).
  const applyPreset = (key: string, days: number) => setFilter((f) => key === 'all'
    ? { ...f, preset: key, start_date: '', end_date: '' }                 // All time → no date filter
    : { ...f, preset: key, start_date: daysAgo(days - 1), end_date: today() });
  const setCustomStart = (v: string) => setFilter((f) => ({ ...f, preset: 'custom', start_date: v }));
  const setCustomEnd = (v: string) => setFilter((f) => ({ ...f, preset: 'custom', end_date: v }));

  // The Model filter offers ALL available models (the catalog from AI Model Settings), unioned
  // with any model already present in this tenant's usage (so nothing is ever missed), deduped.
  const modelOptions = useMemo(() => {
    const list = [...new Set([...catalogModels, ...(summary?.models || [])])].sort();
    return [{ label: 'All models', value: '' }, ...list.map((m) => ({ label: m, value: m }))];
  }, [catalogModels, summary]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const SortHead = ({ field, children, alignRight }: { field: string; children: React.ReactNode; alignRight?: boolean }) => (
    <th className={`px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide select-none cursor-pointer ${alignRight ? 'text-right' : 'text-left'}`} onClick={() => toggleSort(field)}>
      <span className={`inline-flex items-center gap-1 ${alignRight ? 'flex-row-reverse' : ''}`}>
        {children}
        {sort.field === field
          ? (sort.order === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-gray-800" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-800" />)
          : <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />}
      </span>
    </th>
  );

  return (
    <div className="space-y-5 md:space-y-6">
      {/* ── Header ── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-brand-violet-600 to-brand-indigo-600 p-5 md:p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/15 backdrop-blur-sm rounded-xl ring-1 ring-white/20">
              <Coins className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">AI Usage</h1>
              <p className="text-sm text-white/80">How your organization is consuming AI · updated live</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCsv} disabled={exporting || !total}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium bg-white/15 hover:bg-white/25 ring-1 ring-white/20 disabled:opacity-50 transition-colors">
              <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} /> Export CSV
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* ── Period (GLOBAL control — drives the cards, trend & table below) ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-brand-purple-600" />
            <span className="text-sm font-semibold text-gray-800">Period</span>
          </div>
          <DateRangeSelect preset={filter.preset} startDate={filter.start_date} endDate={filter.end_date}
            onPreset={applyPreset} onCustomStart={setCustomStart} onCustomEnd={setCustomEnd} />
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        <KpiCard title="Total Credits" value={fmtCostVal(summary?.total_cost)} icon={<Coins className="w-5 h-5" />} tint="bg-brand-purple-100 text-brand-purple-700" loading={loadingTop} />
        <KpiCard title="Total Tokens" value={fmtTokens(summary?.total_tokens)} icon={<Cpu className="w-5 h-5" />} tint="bg-brand-cyan-100 text-brand-cyan-700" loading={loadingTop} />
        <KpiCard title="Total Requests" value={fmtNumber(summary?.total_requests)} icon={<Activity className="w-5 h-5" />} tint="bg-brand-emerald-100 text-brand-emerald-700" loading={loadingTop} />
        <KpiCard title="Highest Usage Feature" value={summary?.most_used_feature ? featureLabel(summary.most_used_feature) : '—'} icon={<Sparkles className="w-5 h-5" />} tint="bg-brand-violet-100 text-brand-violet-700" loading={loadingTop} />
        <KpiCard title="Highest Usage User" value={summary?.most_active_user ? (
          <span className="block leading-snug" title={userDisplay(summary.most_active_user, summary.most_active_user_email || undefined)}>
            <span className="block text-lg truncate">{summary.most_active_user}</span>
            {summary.most_active_user_email && <span className="block text-xs font-normal text-gray-400 truncate">{summary.most_active_user_email}</span>}
          </span>
        ) : '—'} icon={<Users className="w-5 h-5" />} tint="bg-brand-pink-100 text-brand-pink-700" loading={loadingTop} />
        <KpiCard title="Input / Output Tokens" value={`${fmtTokens(summary?.prompt_tokens)} / ${fmtTokens(summary?.completion_tokens)}`} icon={<ArrowUp className="w-5 h-5" />} tint="bg-slate-100 text-slate-700" loading={loadingTop} />
      </div>

      {/* ── Trend ── */}
      <Panel icon={<TrendingUp className="w-4 h-4 text-brand-purple-600" />} title="Usage Trend"
        right={
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 capitalize hidden sm:inline">{granularity}</span>
            <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
              {([['cost', isCredit ? 'Credits' : 'Cost'], ['tokens', 'Tokens'], ['requests', 'Requests']] as [TrendMetric, string][]).map(([m, lbl]) => (
                <button key={m} onClick={() => setTrendMetric(m)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${trendMetric === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>{lbl}</button>
              ))}
            </div>
          </div>
        }>
        {!loadingTop && trend.length === 0 ? <NoData /> : (
          <LineChart labels={trendLabels} series={trendSeries} height={300} loading={loadingTop}
            valueFormatter={trendFmt} yTickFormatter={axisNum}
            yUnit={trendMetric === 'cost' ? (isCredit ? 'Credits' : 'Dollars') : trendMetric === 'tokens' ? 'Tokens' : 'Requests'}
            xTickAngle={trendLabels.length > 10 ? -35 : undefined} />
        )}
      </Panel>

      {/* ── Records table (with its own drill-down filters) ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-4 md:p-5 border-b border-gray-100 flex flex-wrap items-end gap-3">
          <div className="mr-auto flex items-center gap-2 pb-0.5"><span className="text-sm font-semibold text-gray-800">Records</span></div>
          <div className="w-full md:w-40"><label className={labelCls}>Feature</label>
            <SimpleSelect value={filter.feature_type} onChange={(v) => setFilter((f) => ({ ...f, feature_type: (v as string) || '' }))}
              options={[{ label: 'All features', value: '' }, ...Object.keys(FEATURE_LABELS).map((k) => ({ label: featureLabel(k), value: k }))]} placeholder="All features" /></div>
          <div className="w-full md:w-40"><label className={labelCls}>Model</label>
            <SimpleSelect value={filter.model} onChange={(v) => setFilter((f) => ({ ...f, model: (v as string) || '' }))} options={modelOptions} placeholder="All models" /></div>
          <div className="w-full md:w-32"><label className={labelCls}>Status</label>
            <SimpleSelect value={filter.status} onChange={(v) => setFilter((f) => ({ ...f, status: (v as any) || '' }))}
              options={[{ label: 'All', value: '' }, { label: 'Success', value: 'success' }, { label: 'Failed', value: 'failed' }]} isSearchable={false} /></div>
          <div className="w-full md:w-52"><label className={labelCls}>Search user / email</label>
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Name or email…"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-brand-purple-400" />
            </div>
          </div>
          <button onClick={clearAll} className="text-sm text-gray-500 hover:text-gray-800 font-medium pb-2">Clear</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Feature</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Model</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Tokens</th>
                <SortHead field="total_cost" alignRight>{isCredit ? 'Credits' : 'Cost'}</SortHead>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <SortHead field="createdAt" alignRight>Date</SortHead>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loadingRows ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="animate-pulse"><td className="px-4 py-3" colSpan={8}><div className="h-4 bg-gray-100 rounded w-full" /></td></tr>
                ))
              ) : records.length === 0 ? (
                <tr><td colSpan={8}><NoData label={filter.model ? 'This model hasn’t been used yet' : 'No usage records match these filters'} /></td></tr>
              ) : records.map((r) => (
                <tr key={`${r.reference_id}-${r.date}`} className="hover:bg-gray-50/60 transition-colors">
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 truncate max-w-[300px]" title={userDisplay(r.user_name, r.user_email)}>
                      <span className="font-medium">{r.user_name}</span>
                      {r.user_email && r.user_email !== r.user_name && <span className="text-gray-400"> ({r.user_email})</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{featureLabel(r.feature_type)}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    <div className="truncate max-w-[240px]" title={r.model}>{r.model}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700" title={`${fmtNumber(r.prompt_tokens)} in / ${fmtNumber(r.completion_tokens)} out`}>{fmtTokens(r.total_tokens)}</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900 whitespace-nowrap">{fmtCostVal(r.cost)}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 text-sm text-right text-gray-500 whitespace-nowrap">{formatDate(r.date, 'dd/mm/yyyy')}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setDetail(r)} title="View details"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-brand-purple-600 hover:bg-brand-purple-50 transition-colors">
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > 0 && (
          <div className="border-t border-gray-100">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={total}
              itemsPerPage={pageSize}
              onPageChange={setPage}
              onItemsPerPageChange={(newSize) => {
                setPageSize(newSize);
                setPage(1);
              }}
              itemsPerPageOptions={[10, 20, 50, 100]}
            />
          </div>
        )}
      </div>

      {/* ── Detail drawer (per-call breakdown for one record) ── */}
      {detail && <UsageDetailDrawer record={detail} isCredit={isCredit} onClose={() => setDetail(null)} />}
    </div>
  );
}

/** Slide-over drawer: per-call breakdown for one usage record (input/output/cost/time). */
function UsageDetailDrawer({ record, isCredit, onClose }: { record: TenantUsageRecord; isCredit: boolean; onClose: () => void }) {
  const fmtCost = (v = 0) => (isCredit ? fmtCredits(v) : fmtCurrency(v));
  const rows = record.breakdown || [];
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900">{featureLabel(record.feature_type)}</h3>
              <StatusBadge status={record.status} />
            </div>
            <p className="text-sm text-gray-500 mt-0.5 truncate" title={record.name}>{record.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 shrink-0"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Meta label="User" value={record.user_name} sub={record.user_email} />
            <Meta label="Model" value={record.model} />
            <Meta label="Date" value={formatDateTime(record.date, true)} />
            <Meta label={isCredit ? 'Total Credits' : 'Total Cost'} value={fmtCost(record.cost)} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <TokenStat label="Input" value={record.prompt_tokens} tint="text-emerald-600" />
            <TokenStat label="Output" value={record.completion_tokens} tint="text-amber-600" />
            <TokenStat label="Total" value={record.total_tokens} tint="text-gray-900" />
          </div>
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Per-call breakdown</h4>
            {rows.length === 0 ? (
              <p className="text-sm text-gray-400">No per-call detail available.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Stage</th>
                      <th className="px-3 py-2 text-left font-medium">Service</th>
                      <th className="px-3 py-2 text-right font-medium">In</th>
                      <th className="px-3 py-2 text-right font-medium">Out</th>
                      <th className="px-3 py-2 text-right font-medium">{isCredit ? 'Credits' : 'Cost'}</th>
                      <th className="px-3 py-2 text-right font-medium">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((e, i) => (
                      <tr key={i} className={e.success ? '' : 'bg-red-50/40'}>
                        <td className="px-3 py-2 text-gray-700">{e.stage || '—'}</td>
                        <td className="px-3 py-2 text-gray-500">{e.service_type || '—'}</td>
                        <td className="px-3 py-2 text-right text-emerald-600">{fmtNumber(e.prompt_tokens)}</td>
                        <td className="px-3 py-2 text-right text-amber-600">{fmtNumber(e.completion_tokens)}</td>
                        <td className="px-3 py-2 text-right font-medium text-gray-900 whitespace-nowrap">{fmtCost(e.cost)}</td>
                        <td className="px-3 py-2 text-right text-gray-400 whitespace-nowrap">{formatDateTime(e.time, true)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
      <div className="text-gray-900 font-medium truncate" title={value}>{value}</div>
      {sub && <div className="text-xs text-gray-400 truncate">{sub}</div>}
    </div>
  );
}

function TokenStat({ label, value, tint }: { label: string; value: number; tint: string }) {
  return (
    <div className="rounded-xl bg-gray-50 p-3 text-center">
      <div className={`text-lg font-bold ${tint}`}>{fmtTokens(value)}</div>
      <div className="text-xs text-gray-400 mt-0.5">{label}</div>
    </div>
  );
}

/**
 * Global date-range control: presets (Last 7/30/90 days, 6 months, 1 year, All time) + a
 * custom range. dd/mm/yyyy display over hidden native pickers — the app-standard pattern.
 */
function DateRangeSelect({
  preset, startDate, endDate, onPreset, onCustomStart, onCustomEnd,
}: {
  preset: string; startDate: string; endDate: string;
  onPreset: (key: string, days: number) => void;
  onCustomStart: (v: string) => void; onCustomEnd: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const dateFromRef = useRef<HTMLInputElement>(null);
  const dateToRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fmtShort = (d: string) => { try { return format(new Date(`${d}T00:00:00`), 'MMM d'); } catch { return d; } };
  const label = preset === 'custom'
    ? `${fmtShort(startDate)} – ${fmtShort(endDate)}`
    : (PRESETS.find((p) => p.key === preset)?.label || 'Select range');

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full md:w-60 flex items-center justify-between gap-2 px-3 py-2.5 border border-gray-300 rounded-xl text-sm bg-white text-gray-700 hover:border-brand-purple-300 transition-colors">
        <span className="flex items-center gap-2 truncate"><Calendar className="w-4 h-4 text-gray-400 shrink-0" />{label}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-2 w-72 bg-white rounded-xl border border-gray-200 shadow-lg p-2">
          <div className="space-y-0.5">
            {PRESETS.map((p) => (
              <button key={p.key} onClick={() => { onPreset(p.key, p.days); setOpen(false); }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${preset === p.key ? 'bg-brand-purple-50 text-brand-purple-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}>{p.label}</button>
            ))}
          </div>
          <div className="border-t border-gray-100 mt-2 pt-2">
            <div className={`px-3 mb-1.5 text-xs font-semibold uppercase tracking-wide ${preset === 'custom' ? 'text-brand-purple-600' : 'text-gray-400'}`}>Custom range</div>
            <div className="px-2 pb-1 space-y-2">
              <div>
                <label className="block text-[11px] text-gray-400 mb-1">From</label>
                <div className="relative cursor-pointer" onClick={() => dateFromRef.current?.showPicker()}>
                  <div className="flex items-center gap-2 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                    <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className={startDate ? 'text-gray-900' : 'text-gray-400'}>{startDate ? formatDate(startDate, 'dd/mm/yyyy') : 'dd/mm/yyyy'}</span>
                  </div>
                  <input ref={dateFromRef} type="date" value={startDate} max={endDate || today()}
                    onChange={(e) => onCustomStart(e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] text-gray-400 mb-1">To</label>
                <div className="relative cursor-pointer" onClick={() => dateToRef.current?.showPicker()}>
                  <div className="flex items-center gap-2 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                    <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className={endDate ? 'text-gray-900' : 'text-gray-400'}>{endDate ? formatDate(endDate, 'dd/mm/yyyy') : 'dd/mm/yyyy'}</span>
                  </div>
                  <input ref={dateToRef} type="date" value={endDate} min={startDate} max={today()}
                    onChange={(e) => onCustomEnd(e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
